import { createAsyncThunk } from '@reduxjs/toolkit'
import { goalsAPI } from '../../utils/supabase'

// Generate temporary ID for optimistic updates
const generateTempId = () => `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`

// Weekly Goals Thunks
export const fetchWeeklyGoals = createAsyncThunk(
  'goals/fetchWeeklyGoals',
  async (userId, { rejectWithValue }) => {
    try {
      const { data, error } = await goalsAPI.getAllWeeklyGoals(userId)
      if (error) throw error
      return data
    } catch (error) {
      return rejectWithValue(error.message)
    }
  }
)

export const createWeeklyGoal = createAsyncThunk(
  'goals/createWeeklyGoal',
  async ({ userId, goalData }, { rejectWithValue, getState }) => {
    const tempId = generateTempId()
    
    try {
      const { data, error } = await goalsAPI.createWeeklyGoal(userId, goalData)
      if (error) throw error
      return { 
        data, 
        tempId,
        type: 'create'
      }
    } catch (error) {
      return rejectWithValue({ 
        message: error.message, 
        tempId,
        type: 'create'
      })
    }
  }
)

export const updateWeeklyGoal = createAsyncThunk(
  'goals/updateWeeklyGoal',
  async ({ id, ...updates }, { rejectWithValue, getState }) => {
    const state = getState()
    const originalGoal = state.goals.weeklyGoals.find(goal => goal.id === id)
    
    try {
      const { data, error } = await goalsAPI.updateWeeklyGoal(id, updates)
      if (error) throw error
      return { 
        data, 
        originalGoal,
        type: 'update'
      }
    } catch (error) {
      return rejectWithValue({ 
        message: error.message, 
        id,
        originalGoal,
        type: 'update'
      })
    }
  }
)

export const deleteWeeklyGoal = createAsyncThunk(
  'goals/deleteWeeklyGoal',
  async (id, { rejectWithValue, getState }) => {
    const state = getState()
    const goalToDelete = state.goals.weeklyGoals.find(goal => goal.id === id)
    
    try {
      const { error } = await goalsAPI.deleteWeeklyGoal(id)
      if (error) throw error
      return { 
        id, 
        deletedGoal: goalToDelete,
        type: 'delete'
      }
    } catch (error) {
      return rejectWithValue({ 
        message: error.message, 
        id,
        deletedGoal: goalToDelete,
        type: 'delete'
      })
    }
  }
)

// Weekly Goals Reducers with Optimistic Updates
export const weeklyGoalsReducers = {
  // Fetch Weekly Goals
  [fetchWeeklyGoals.pending]: (state) => {
    state.loading.weekly = true
    state.error.weekly = null
  },
  [fetchWeeklyGoals.fulfilled]: (state, action) => {
    state.loading.weekly = false
    state.weeklyGoals = action.payload
    state.initialized.weekly = true
  },
  [fetchWeeklyGoals.rejected]: (state, action) => {
    state.loading.weekly = false
    state.error.weekly = action.payload
  },

  // Create Weekly Goal - Optimistic
  [createWeeklyGoal.pending]: (state, action) => {
    const { userId, goalData } = action.meta.arg
    const tempId = generateTempId()
    
    const optimisticGoal = {
      ...goalData,
      id: tempId,
      user_id: userId,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      status: goalData.status || 'draft',
      isOptimistic: true,
      isLoading: true
    }
    
    state.weeklyGoals.unshift(optimisticGoal)
    state.error.weekly = null
  },
  [createWeeklyGoal.fulfilled]: (state, action) => {
    const { data } = action.payload
    const tempIndex = state.weeklyGoals.findIndex(goal => goal.isOptimistic && goal.isLoading)
    
    if (tempIndex !== -1) {
      // Replace optimistic goal with real data
      state.weeklyGoals[tempIndex] = {
        ...data,
        isOptimistic: false,
        isLoading: false
      }
    } else {
      // Fallback: just add the new goal
      state.weeklyGoals.unshift(data)
    }
  },
  [createWeeklyGoal.rejected]: (state, action) => {
    // Remove the optimistic goal
    state.weeklyGoals = state.weeklyGoals.filter(goal => 
      !goal.isOptimistic || !goal.isLoading
    )
    state.error.weekly = action.payload.message
  },

  // Update Weekly Goal - Optimistic
  [updateWeeklyGoal.pending]: (state, action) => {
    const { id, ...updates } = action.meta.arg
    const index = state.weeklyGoals.findIndex(goal => goal.id === id)
    
    if (index !== -1) {
      // Apply optimistic update
      state.weeklyGoals[index] = {
        ...state.weeklyGoals[index],
        ...updates,
        updated_at: new Date().toISOString(),
        isOptimistic: true,
        isLoading: true
      }
    }
    state.error.weekly = null
  },
  [updateWeeklyGoal.fulfilled]: (state, action) => {
    const { data } = action.payload
    const index = state.weeklyGoals.findIndex(goal => goal.id === data.id)
    
    if (index !== -1) {
      state.weeklyGoals[index] = {
        ...data,
        isOptimistic: false,
        isLoading: false
      }
    }
  },
  [updateWeeklyGoal.rejected]: (state, action) => {
    const { originalGoal, id } = action.payload
    const index = state.weeklyGoals.findIndex(goal => goal.id === id)
    
    if (index !== -1 && originalGoal) {
      // Rollback to original state
      state.weeklyGoals[index] = {
        ...originalGoal,
        isOptimistic: false,
        isLoading: false
      }
    }
    state.error.weekly = action.payload.message
  },

  // Delete Weekly Goal - Optimistic
  [deleteWeeklyGoal.pending]: (state, action) => {
    const id = action.meta.arg
    const index = state.weeklyGoals.findIndex(goal => goal.id === id)
    
    if (index !== -1) {
      // Mark as deleting instead of removing immediately
      state.weeklyGoals[index] = {
        ...state.weeklyGoals[index],
        isOptimistic: true,
        isLoading: true,
        isDeleting: true
      }
    }
    state.error.weekly = null
  },
  [deleteWeeklyGoal.fulfilled]: (state, action) => {
    const { id } = action.payload
    // Permanently remove the goal
    state.weeklyGoals = state.weeklyGoals.filter(goal => goal.id !== id)
  },
  [deleteWeeklyGoal.rejected]: (state, action) => {
    const { id, deletedGoal } = action.payload
    const index = state.weeklyGoals.findIndex(goal => goal.id === id)
    
    if (index !== -1) {
      // Restore the goal (remove deleting state)
      state.weeklyGoals[index] = {
        ...deletedGoal,
        isOptimistic: false,
        isLoading: false,
        isDeleting: false
      }
    } else if (deletedGoal) {
      // If somehow removed, add it back
      state.weeklyGoals.push({
        ...deletedGoal,
        isOptimistic: false,
        isLoading: false,
        isDeleting: false
      })
    }
    state.error.weekly = action.payload.message
  }
}
